import { EM_DASH } from '../lib/typography';
import { Component, createRef } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useRef } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import {
  parseSearchQuery, applyTokensToState, getActiveTokenContext,
  getMatchingKeywords, getMatchingEnumValues, getElementLetterSuggestions,
  nextHighlightIndex,
} from '../utils/sorcery/searchSyntax';
import {
  NUMERIC_OPERATORS, UMBRELLAS,
  createEmptyFilterState, buildKeywordIndex, buildSubtypeOptions, buildArtistOptions,
  buildCardMatcher, buildFoilingMatcher,
} from '../utils/sorcery/cardFilterLogic';
import ElementIcon from './deckStats/ElementIcon';
import { isPhoneLayout } from '../utils/platform';
import { isFreePlay } from '../utils/freePlay';
import { MultiSelect } from './ui/multi-select';
import { Select } from './ui/select';
import { Button } from './ui/button';
import { t, k } from '../localization/t';
import {
  GOLD, TEXT_BODY, ACCENT_GOLD, CornerPlating,
} from '../lib/medievalTheme';

// Sampled once at module load. An OS-level setting; Electron restarts on change.
const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Session-constant (see platform.js). On phone the bar compresses to a
// strip (search + element chips + scope) and the advanced popover becomes
// a full-screen sheet that also hosts the Type pills.
const PHONE = isPhoneLayout();

// Search-syntax autocomplete enter/exit. Both dropdown variants (hint + list)
// share one key so switching between them while typing swaps content in place.
// the animation only plays on true open/close. Exit faster than entry.
const SUGGESTIONS_MOTION = {
  initial: REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, y: -6 },
  animate: REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, y: 0 },
  exit: REDUCED_MOTION
    ? { opacity: 0, transition: { duration: 0.1 } }
    : { opacity: 0, y: -4, transition: { duration: 0.1, ease: [0.22, 1, 0.36, 1] } },
  transition: { duration: 0.14, ease: [0.22, 1, 0.36, 1] },
};

// Dropdown headings, keyed by suggestion mode. The 'hint' mode renders its
// own panel and never reaches these.
const SUGGESTION_HEADINGS = {
  prefix: () => t('Search syntax'),
  value: (ctx) => t('{prefix}: values', { prefix: ctx.keyword.prefix }),
  element: () => t('Element letters, combine to stack'),
};

export function SorceryElementIcon({ element, className = 'size-3.5' }) {
  const triangles = {
    Water: { points: '6,11 1,2 11,2', line: null, color: '#01FFFF' },
    Earth: { points: '6,11 1,2 11,2', line: [2.5, 5, 9.5, 5], color: '#CFA572' },
    Fire: { points: '6,1 11,10 1,10', line: null, color: '#FF5F00' },
    Air: { points: '6,1 11,10 1,10', line: [2.5, 7, 9.5, 7], color: '#A0BADB' },
  };
  const tri = triangles[element];
  if (!tri) return null;
  return (
    <svg viewBox="0 0 12 12" className={className} fill="none" stroke={tri.color} strokeWidth="1.5" strokeLinejoin="round">
      <polygon points={tri.points} />
      {tri.line ? <line x1={tri.line[0]} y1={tri.line[1]} x2={tri.line[2]} y2={tri.line[3]} /> : null}
    </svg>
  );
}

// Visual styles for chip buttons live in `.filter-chip` CSS (see
// index.css). Callers attach the class and set `data-active` to drive
// the selected variant; hover and pressed states come from the
// pseudo-classes. The function exists so consumers can spread the
// returned props onto a <button> in one call. No inline cursor. The
// global pointer cursor rule applies to <button> already.
function chipProps(active, extraClassName) {
  const className = extraClassName ? `filter-chip ${extraClassName}` : 'filter-chip';
  return { className, 'data-active': active ? 'true' : 'false' };
}

// Hover-expanding chip for filter categories with multiple values.
function GroupedChip({ category, items, onRemoveAll }) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };
  const handleEnter = () => { cancelClose(); setOpen(true); };
  const handleLeave = () => scheduleClose();

  return (
    <span className="relative inline-block align-middle" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 text-[0.6875cqw] font-semibold invisible"
        style={{ lineHeight: 1.2 }}
        aria-hidden="true"
      >
        <span className="text-[0.625cqw]">{category}</span>
        <span className="text-[0.6875cqw]">{items.length}</span>
      </span>
      <motion.div
        layout
        initial={false}
        transition={{ type: 'spring', stiffness: 480, damping: 38, mass: 0.55 }}
        className="absolute top-0 left-0 z-30 overflow-hidden inline-flex items-stretch"
        style={{
          background: open ? 'rgba(8,6,4,0.97)' : 'rgba(12,10,8,0.92)',
          border: `1px solid ${GOLD} ${open ? '0.5' : '0.3'})`,
          borderRadius: PHONE ? '0.375rem' : (open ? '0.5cqw' : '0.375cqw'),
          color: '#d7c896',
          fontSize: PHONE ? '0.6875rem' : '0.6875cqw',
          lineHeight: 1.2,
          boxShadow: open
            ? '0 10px 26px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,220,140,0.06)'
            : 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, border-radius 0.18s ease',
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {!open ? (
            <motion.div
              key="collapsed"
              layout="position"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-[0.6875cqw] font-semibold"
              style={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}
            >
              <span className="opacity-55 text-[0.625cqw]">{category}</span>
              <span className="text-[0.6875cqw]" style={{ color: '#f4e4b8' }}>{items.length}</span>
            </motion.div>
          ) : (
            <motion.div
              key="expanded"
              layout="position"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
              className="flex flex-col p-1 min-w-[8.75cqw]"
              style={{ whiteSpace: 'nowrap' }}
            >
              <div
                className="flex items-center justify-between gap-3 px-1.5 py-1 mb-0.5"
                style={{ borderBottom: `1px solid ${GOLD} 0.18)` }}
              >
                <span className="text-[0.5625cqw] font-bold opacity-65" style={{ color: '#d7c896' }}>{category}</span>
                <button
                  type="button"
                  onClick={onRemoveAll}
                  title={t('Remove all {category} filters', { category })}
                  aria-label={t('Remove all {category} filters', { category })}
                  className="inline-flex items-center justify-center rounded transition-all cursor-pointer shrink-0"
                  style={{
                    width: PHONE ? '1.5rem' : '1cqw', height: PHONE ? '1.5rem' : '1cqw',
                    background: 'rgba(180,60,60,0.2)',
                    border: '1px solid rgba(220,120,120,0.45)',
                    color: '#ffb0b0',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(180,60,60,0.42)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(180,60,60,0.2)'; }}
                >
                  <X className="size-2.5" strokeWidth={2.6} />
                </button>
              </div>
              <div className="flex flex-col">
                {items.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={item.onRemove}
                    className="group/row w-full flex items-center justify-between gap-3 px-1.5 py-1 rounded text-[0.625cqw] font-medium transition-all cursor-pointer"
                    style={{ color: '#f4e4b8' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.12)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span className="truncate">{item.value}</span>
                    <X className="size-2.5 opacity-40 group-hover/row:opacity-95 shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </span>
  );
}

// One field family for the advanced panel: same height, same radius and
// the same recessed dark fill on every control, so selects, number boxes,
// text inputs and multi-selects read as one row of inputs instead of a
// mix of raised buttons and sunken fields.
const FIELD_HEIGHT = PHONE ? '2.5rem' : '2cqw';
const FIELD_RADIUS = PHONE ? '0.375rem' : '0.375cqw';
// Panel padding, and the outer radius concentric with it: every control
// sits one padding-step in from the panel edge, so outer = inner + pad.
const PANEL_PAD = PHONE ? '0.75rem' : '0.625cqw';
const PANEL_RADIUS = PHONE ? '0' : '1cqw';
const FIELD_STYLE = {
  height: FIELD_HEIGHT,
  borderRadius: FIELD_RADIUS,
  background: '#080604',
  border: `1px solid ${GOLD} 0.22)`,
  color: '#f4e4b8',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
  textShadow: 'none',
};

// Field caption: quiet neutral, so the gold section headings stay the
// only thing competing for attention. Nine gold "COST / ATTACK / …"
// captions next to nine gold section labels is what made the panel read
// as noise.
const CAPTION_CLASS = PHONE
  ? 'block text-[0.6875rem] font-semibold mb-1'
  : 'block text-[0.5cqw] font-semibold mb-1';
const CAPTION_STYLE = { color: 'rgba(166, 160, 155, 0.6)' };
// Numeric fields get a fixed-height caption slot: the threshold captions lead
// with an element pip, and a pip is taller than a 0.5cqw line of text.
// without the fixed height the fields in a row start at different y.
const PIP_SIZE = 13; // ElementIcon takes px-at-scale-1 and converts to cqw
const NUMERIC_CAPTION_CLASS = PHONE
  ? 'flex items-center h-[1rem] text-[0.5cqw] font-semibold mb-1'
  : 'flex items-center h-[0.8125cqw] text-[0.5cqw] font-semibold mb-1';
// Chips inside the panel run one step tighter than the toolbar chips so
// the five-chip Set and Rarity groups each hold a single line at half
// the panel width.
// The `!` important prefix beats the phone rem remap table, so the phone
// variant restates the size in rem directly.
const PANEL_CHIP = PHONE ? '!text-[0.75rem] !px-2' : '!text-[0.625cqw] !px-1.5';

// The trigger shows the operator as a symbol, the menu keeps the words.
// Six "Equals" selects side by side is the single busiest thing in the
// panel, and the symbol is what players actually read.
const OP_SYMBOLS = { '=': '=', '!=': '≠', '<': '<', '<=': '≤', '>': '>', '>=': '≥' };

// The umbrella keys and the finish filter values double as their own labels;
// both get a table so the string extractor can see them.
const UMBRELLA_LABEL = { Evil: k('Evil'), Royalty: k('Royalty'), Knight: k('Knight') };
const FOIL_FILTER_LABEL = { standard: k('Standard'), foil: k('Foil') };
const opOptions = () => NUMERIC_OPERATORS.map((o) => ({
  ...o,
  label: `${OP_SYMBOLS[o.value]}   ${t(o.label)}`,
  triggerLabel: OP_SYMBOLS[o.value],
}));

function NumericFilter({ label, element, value, onChange, min = 0, max = 99 }) {
  const handleOp = (op) => onChange({ ...value, op });
  const handleValue = (e) => {
    const raw = e.target.value;
    if (raw === '') return onChange({ ...value, value: null });
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    onChange({ ...value, value: Math.max(min, Math.min(max, n)) });
  };
  return (
    <div>
      <span className={NUMERIC_CAPTION_CLASS} style={CAPTION_STYLE}>
        {element ? <ElementIcon element={element} size={PHONE ? '0.875rem' : PIP_SIZE} className="mr-1" /> : null}
        {label}
      </span>
      {/* Operator and value are one segmented control, not two floating
          pills. A 2cqw select next to a 2cqw input reads as debris. */}
      <div
        className="flex items-stretch overflow-hidden transition-colors duration-100 focus-within:border-[rgba(212,168,67,0.5)]"
        style={{ ...FIELD_STYLE, width: PHONE ? '7rem' : '5.25cqw' }}
      >
        <Select
          ariaLabel={t('{label} comparison', { label })}
          options={opOptions()}
          value={value.op}
          onValueChange={handleOp}
          portalMenu
          menuPreferredWidth={150}
          triggerClassName="!px-1 !justify-center gap-0.5 !text-[0.6875cqw] [&_svg]:size-2.5"
          triggerStyle={{
            height: '100%',
            width: PHONE ? '2.5rem' : '2.5cqw',
            background: 'transparent',
            border: 'none',
            borderRight: `1px solid ${GOLD} 0.18)`,
            borderRadius: 0,
            boxShadow: 'none',
            color: '#f4e4b8',
            textShadow: 'none',
          }}
          className="shrink-0"
        />
        <input
          type="number"
          min={min}
          max={max}
          value={value.value ?? ''}
          placeholder={EM_DASH}
          onInput={handleValue}
          className="flex-1 min-w-0 bg-transparent text-[0.625cqw] px-1 text-center outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{ color: '#f4e4b8' }}
          aria-label={t('{label} value', { label })}
        />
      </div>
    </div>
  );
}

function TextFilter({ label, value, onChange, placeholder }) {
  return (
    <label className="block w-full">
      <span className={CAPTION_CLASS} style={CAPTION_STYLE}>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder || ''}
        onInput={(e) => onChange(e.target.value)}
        className="w-full min-w-0 text-[0.625cqw] px-2 outline-none transition-colors duration-100 focus:border-[rgba(212,168,67,0.5)]"
        style={FIELD_STYLE}
      />
    </label>
  );
}

// Reusable filter bar: search input + search-syntax autocomplete,
// element/type pills, advanced-filters popover, and active-filter chips.
// Consumers subscribe via `onChange(api)` which emits `{ state,
// matchesCard, matchesFoiling }`. `matchesCard` already incorporates
// scope when `features.scope` is enabled and `scopeData` is provided;
// `matchesFoiling` is separate because foiling is per-entry.
export default class CardFilterBar extends Component {
  static defaultProps = {
    features: { scope: false, finish: true, type: true },
    initialState: null,
    scopeData: null,
  };

  constructor(props) {
    super(props);
    const base = createEmptyFilterState();
    this.state = {
      ...base,
      ...(props.initialState || {}),
      // Sets survive JSON-ish initialState if the caller normalizes them.
      elementFilters: coerceSet(props.initialState?.elementFilters) || base.elementFilters,
      typeFilters: coerceSet(props.initialState?.typeFilters) || base.typeFilters,
      setFilters: coerceSet(props.initialState?.setFilters) || base.setFilters,
      rarityFilters: coerceSet(props.initialState?.rarityFilters) || base.rarityFilters,
      keywordFilters: coerceSet(props.initialState?.keywordFilters) || base.keywordFilters,
      subtypeFilters: coerceSet(props.initialState?.subtypeFilters) || base.subtypeFilters,
      umbrellaFilters: coerceSet(props.initialState?.umbrellaFilters) || base.umbrellaFilters,
      artistFilters: coerceSet(props.initialState?.artistFilters) || base.artistFilters,
      advancedOpen: false,
      advancedMaxHeight: null,
      advancedMaxWidth: null,
      searchSuggestionsOpen: false,
      searchCursor: 0,
      searchHighlightIdx: 0,
    };
    this.searchInputRef = createRef();
    this.suggestionListRef = createRef();
    this._keywordCacheKey = null;
    this._keywordIndex = new Map();
    this._keywordOptions = [];
    this._subtypeCacheKey = null;
    this._subtypeOptions = [];
    this._artistCacheKey = null;
    this._artistOptions = [];
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleGlobalKeyDown);
    window.addEventListener('resize', this._handleViewportResize);
    document.addEventListener('mousedown', this.handleAdvancedOutsideClick);
    this.emitChange({ userInitiated: false });
  }

  componentDidUpdate(prevProps, prevState) {
    const filterChanged = filterStateChanged(prevState, this.state);
    const propsChanged = prevProps.sorceryCards !== this.props.sorceryCards ||
        prevProps.scopeData !== this.props.scopeData;
    if (filterChanged || propsChanged) {
      // userInitiated tells consumers whether the emit reflects a real
      // filter action (typing, toggling a pill, switching scope) vs. a
      // re-emit caused by upstream prop churn (e.g. scopeData refreshing
      // because the deck contents changed). Consumers like
      // DeckEditorCollection use this to decide whether to reset scroll.
      this.emitChange({ userInitiated: filterChanged });
    }
    if (prevState.searchHighlightIdx !== this.state.searchHighlightIdx
        || prevState.searchSuggestionsOpen !== this.state.searchSuggestionsOpen) {
      this._scrollHighlightIntoView();
    }
    if (!prevState.advancedOpen && this.state.advancedOpen) {
      this._measureAdvancedDimensions();
      this._escDismissId = pushDismiss(() => this.setState({ advancedOpen: false }));
    } else if (prevState.advancedOpen && !this.state.advancedOpen) {
      removeDismiss(this._escDismissId);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeyDown);
    window.removeEventListener('resize', this._handleViewportResize);
    document.removeEventListener('mousedown', this.handleAdvancedOutsideClick);
    removeDismiss(this._escDismissId);
  }

  // `block: 'nearest'` scrolls the minimum distance needed, so walking the
  // list with ↓ creeps one row at a time instead of centring (and jolting)
  // on every keypress. Wrapping from last to first still jumps, which is
  // the correct read of "you went back to the top".
  _scrollHighlightIntoView() {
    const list = this.suggestionListRef.current;
    if (!list) return;
    const row = list.children[this.state.searchHighlightIdx];
    row?.scrollIntoView({ block: 'nearest' });
  }

  _handleViewportResize = () => {
    if (this.state.advancedOpen) this._measureAdvancedDimensions();
  };

  // Clamp the advanced panel to the actual space below+right-of its trigger.
  _measureAdvancedDimensions = () => {
    if (PHONE) return; // the phone sheet is full-screen; nothing to clamp
    const anchor = this._advancedAnchorEl || this._advancedButtonEl;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const verticalReserve = 24; // panel mt-2 + safety
    const horizontalReserve = 16;
    const availableHeight = window.innerHeight - rect.bottom - verticalReserve;
    const availableWidth = window.innerWidth - rect.left - horizontalReserve;
    this.setState({
      advancedMaxHeight: Math.max(220, availableHeight),
      advancedMaxWidth: Math.max(280, availableWidth),
    });
  };

  emitChange = ({ userInitiated = false } = {}) => {
    const { onChange } = this.props;
    if (!onChange) return;
    const { index: keywordIndex } = this._getKeywordIndex();
    const scopeData = this.props.features?.scope ? this.props.scopeData : null;
    const matchesCard = buildCardMatcher(this.state, { keywordIndex, scopeData });
    const matchesFoiling = buildFoilingMatcher(this.state);
    onChange({ state: this.state, matchesCard, matchesFoiling, isActive: this.hasAnyActive(), userInitiated });
  };

  hasAnyActive = () => {
    const s = this.state;
    if (s.searchQuery.trim()) return true;
    if (s.elementFilters.size || s.typeFilters.size || s.setFilters.size || s.rarityFilters.size) return true;
    if (s.keywordFilters.size || s.subtypeFilters.size || s.umbrellaFilters.size || s.artistFilters.size) return true;
    if (s.rulesQuery.trim() || s.flavorQuery.trim()) return true;
    if (s.cardScope && s.cardScope !== 'all') return true;
    if (s.foilFilter && s.foilFilter !== 'all') return true;
    for (const k of ['costFilter', 'attackFilter', 'defenseFilter', 'lifeFilter', 'anyThresholdFilter', 'airThresholdFilter', 'earthThresholdFilter', 'fireThresholdFilter', 'waterThresholdFilter']) {
      if (s[k]?.value != null) return true;
    }
    return false;
  };

  handleAdvancedOutsideClick = (e) => {
    if (!this.state.advancedOpen) return;
    if (this._advancedPanelEl?.contains(e.target)) return;
    if (this._advancedButtonEl?.contains(e.target)) return;
    if (e.target?.closest?.('[role="listbox"], [role="menu"]')) return;
    this.setState({ advancedOpen: false });
  };

  handleGlobalKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F')) {
      e.preventDefault();
      const el = this.searchInputRef.current;
      if (el) {
        el.focus();
        try { el.select(); } catch (_) { /* ignore */ }
      }
      return;
    }
    // Escape dismissal of the advanced-filters popover goes through the
    // central Esc stack (registered in componentDidUpdate while open). The
    // search-suggestions dropdown still handles its own Escape in
    // `_handleSearchKeyDown` while the input has focus.
  };

  _getKeywordIndex() {
    const cards = this.props.sorceryCards || [];
    if (this._keywordCacheKey === cards) {
      return { index: this._keywordIndex, options: this._keywordOptions };
    }
    const { index, options } = buildKeywordIndex(cards);
    this._keywordCacheKey = cards;
    this._keywordIndex = index;
    this._keywordOptions = options;
    return { index, options };
  }

  _getSubtypeOptions() {
    const cards = this.props.sorceryCards || [];
    if (this._subtypeCacheKey === cards) return this._subtypeOptions;
    this._subtypeCacheKey = cards;
    this._subtypeOptions = buildSubtypeOptions(cards);
    return this._subtypeOptions;
  }

  _getArtistOptions() {
    const cards = this.props.sorceryCards || [];
    if (this._artistCacheKey === cards) return this._artistOptions;
    this._artistCacheKey = cards;
    this._artistOptions = buildArtistOptions(cards);
    return this._artistOptions;
  }

  toggleFilter = (key, value) => {
    this.setState((s) => {
      const next = new Set(s[key]);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { [key]: next };
    });
  };

  clearFilter = (key) => this.setState({ [key]: new Set() });

  _resetAllFilters = () => {
    const base = createEmptyFilterState();
    this.setState({
      ...base,
      advancedOpen: this.state.advancedOpen,
      searchSuggestionsOpen: false,
      searchCursor: 0,
      searchHighlightIdx: 0,
    });
  };

  _getDynamicValueOptions(keyword) {
    if (!keyword) return [];
    if (keyword.filterKey === 'keywordFilters') {
      const { options } = this._getKeywordIndex();
      return options.map((o) => o.label || o.value);
    }
    if (keyword.filterKey === 'subtypeFilters') {
      return this._getSubtypeOptions().map((o) => o.label || o.value);
    }
    if (keyword.filterKey === 'artistFilters') {
      return this._getArtistOptions().map((o) => o.label || o.value);
    }
    return [];
  }

  _getSearchSuggestions() {
    const { searchQuery, searchCursor } = this.state;
    if (!searchQuery) return { mode: 'prefix', items: getMatchingKeywords(''), context: { kind: 'empty', tokenStart: 0, tokenEnd: 0 } };
    const ctx = getActiveTokenContext(searchQuery, searchCursor);
    if (ctx.kind === 'prefix') {
      return { mode: 'prefix', items: getMatchingKeywords(ctx.fragment), context: ctx };
    }
    if (ctx.kind === 'value') {
      const k = ctx.keyword;
      if (k.kind === 'enum' || k.kind === 'single-enum') {
        let values = getMatchingEnumValues(k, ctx.valueFragment);
        // Callers (e.g. StoreSinglesTab) can restrict `rarity:` autocomplete
        // to a subset. This is how Spellcraft hides "Unique" from the
        // rarity dropdown alongside the advanced-filter pill.
        if (k.filterKey === 'rarityFilters' && Array.isArray(this.props.rarityOptions)) {
          const allowed = new Set(this.props.rarityOptions);
          values = values.filter((v) => allowed.has(v));
        }
        return { mode: 'value', items: values.map((v) => ({ label: v, insert: v.includes(' ') ? `"${v}"` : v })), context: ctx };
      }
      if (k.kind === 'element') {
        return { mode: 'element', items: getElementLetterSuggestions(ctx.valueFragment).map((e) => ({ label: e.letter, detail: e.name, insert: e.letter, append: true })), context: ctx };
      }
      if (k.kind === 'multi-string') {
        const all = this._getDynamicValueOptions(k);
        const q = (ctx.valueFragment || '').toLowerCase();
        const filtered = q ? all.filter((v) => v.toLowerCase().includes(q)) : all;
        return {
          mode: 'value',
          items: filtered.map((v) => ({ label: v, insert: v.includes(' ') ? `"${v}"` : v })),
          context: ctx,
        };
      }
      return { mode: 'hint', items: [], context: ctx, hint: k.description };
    }
    return { mode: 'prefix', items: [], context: ctx };
  }

  _commitSearchTokens = () => {
    const { searchQuery } = this.state;
    const { tokens, leftover } = parseSearchQuery(searchQuery);
    if (tokens.length === 0) {
      this.setState({ searchSuggestionsOpen: false });
      return;
    }
    const updates = applyTokensToState(tokens, this.state);
    // Drop scope updates silently when the feature is disabled. Users
    // typing scope:* in auction house shouldn't enable a filter the host
    // can't interpret.
    if (!this.props.features?.scope && 'cardScope' in updates) delete updates.cardScope;
    const extra = updates._extraName;
    delete updates._extraName;
    const nextSearch = [extra, leftover].filter(Boolean).join(' ').trim();
    this.setState({
      ...updates,
      searchQuery: nextSearch,
      searchCursor: nextSearch.length,
      searchSuggestionsOpen: false,
      searchHighlightIdx: 0,
    });
  };

  _applySuggestion = (item, ctx, mode) => {
    const { searchQuery } = this.state;
    let nextText, cursor;
    const shouldAutoCommit = (mode === 'value' || mode === 'element');
    if (mode === 'prefix') {
      const replacement = `${item.prefix}:`;
      nextText = searchQuery.slice(0, ctx.tokenStart) + replacement + searchQuery.slice(ctx.tokenEnd);
      cursor = ctx.tokenStart + replacement.length;
    } else if (mode === 'value' || mode === 'element') {
      nextText = searchQuery.slice(0, ctx.valueStart) + item.insert + searchQuery.slice(ctx.tokenEnd);
      cursor = ctx.valueStart + item.insert.length;
    } else {
      return;
    }
    this.setState({ searchQuery: nextText, searchCursor: cursor, searchHighlightIdx: 0 }, () => {
      const el = this.searchInputRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(cursor, cursor); } catch (_) { /* ignore */ }
      }
      if (shouldAutoCommit) this._commitSearchTokens();
    });
  };

  _handleSearchKeyDown = (e) => {
    const { searchSuggestionsOpen, searchHighlightIdx } = this.state;
    const sug = this._getSearchSuggestions();
    const itemCount = sug.items.length;

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && itemCount > 0) {
      e.preventDefault();
      this.setState({
        searchSuggestionsOpen: true,
        searchHighlightIdx: nextHighlightIndex(searchHighlightIdx, itemCount, e.key, searchSuggestionsOpen),
      });
      return;
    }
    if (e.key === 'Tab' && searchSuggestionsOpen && itemCount > 0) {
      e.preventDefault();
      const item = sug.items[Math.min(searchHighlightIdx, itemCount - 1)];
      this._applySuggestion(item, sug.context, sug.mode);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchSuggestionsOpen && itemCount > 0 && sug.mode !== 'hint') {
        const item = sug.items[Math.min(searchHighlightIdx, itemCount - 1)];
        this._applySuggestion(item, sug.context, sug.mode);
      } else {
        this._commitSearchTokens();
      }
      return;
    }
    if (e.key === 'Escape') {
      if (searchSuggestionsOpen) {
        e.preventDefault();
        this.setState({ searchSuggestionsOpen: false });
      }
    }
  };

  _buildActiveChips() {
    const {
      typeFilters,
      setFilters, rarityFilters, keywordFilters, subtypeFilters, umbrellaFilters,
      cardScope, foilFilter,
      rulesQuery, flavorQuery, artistFilters,
      costFilter, attackFilter, defenseFilter, lifeFilter,
      anyThresholdFilter, airThresholdFilter, earthThresholdFilter, fireThresholdFilter, waterThresholdFilter,
    } = this.state;
    const features = this.props.features || {};
    const groups = [];
    const addSet = (label, key, set, format = (v) => v) => {
      if (set.size === 0) return;
      groups.push({
        key,
        category: label,
        items: Array.from(set).map((v) => ({
          value: format(v),
          onRemove: () => this.toggleFilter(key, v),
        })),
        onRemoveAll: () => this.clearFilter(key),
      });
    };
    const addNumeric = (label, key, filter, defaultValue = { op: '=', value: null }) => {
      if (filter.value === null || filter.value === undefined) return;
      const opLabel = t(NUMERIC_OPERATORS.find((o) => o.value === filter.op)?.label || filter.op);
      const reset = () => this.setState({ [key]: defaultValue });
      groups.push({
        key,
        category: label,
        items: [{ value: `${opLabel} ${filter.value}`, onRemove: reset }],
        onRemoveAll: reset,
      });
    };
    const addText = (label, key, value) => {
      if (!value || !value.trim()) return;
      const reset = () => this.setState({ [key]: '' });
      groups.push({
        key,
        category: label,
        items: [{ value: `"${value.trim()}"`, onRemove: reset }],
        onRemoveAll: reset,
      });
    };

    // On phone the Type pills live inside the filter sheet, so an active
    // type filter needs a removable chip to stay visible (and counted in
    // the badge). Desktop shows the pills in the bar itself.
    if (PHONE && features.type !== false) addSet(t('Type'), 'typeFilters', typeFilters, (v) => t(v));
    addSet(t('Set'), 'setFilters', setFilters, (v) => v.charAt(0).toUpperCase() + v.slice(1));
    addSet(t('Rarity'), 'rarityFilters', rarityFilters, (v) => t(v));
    addSet(t('Subtype'), 'subtypeFilters', subtypeFilters);
    addSet(t('Keyword'), 'keywordFilters', keywordFilters);
    addSet(t('Umbrella'), 'umbrellaFilters', umbrellaFilters);
    addText(t('Rules'), 'rulesQuery', rulesQuery);
    addText(t('Flavor'), 'flavorQuery', flavorQuery);
    addSet(t('Artist'), 'artistFilters', artistFilters);
    addNumeric(t('Cost'), 'costFilter', costFilter);
    addNumeric(t('Attack'), 'attackFilter', attackFilter);
    addNumeric(t('Defense'), 'defenseFilter', defenseFilter);
    addNumeric(t('Life'), 'lifeFilter', lifeFilter);
    addNumeric(t('Threshold'), 'anyThresholdFilter', anyThresholdFilter);
    addNumeric(t('Air Th.'), 'airThresholdFilter', airThresholdFilter);
    addNumeric(t('Earth Th.'), 'earthThresholdFilter', earthThresholdFilter);
    addNumeric(t('Fire Th.'), 'fireThresholdFilter', fireThresholdFilter);
    addNumeric(t('Water Th.'), 'waterThresholdFilter', waterThresholdFilter);

    if (features.scope !== false && cardScope && cardScope !== 'all') {
      const label = cardScope === 'deck' ? t('In Deck') : t('Owned');
      const reset = () => this.setState({ cardScope: 'all' });
      groups.push({ key: 'scope', category: t('Scope'), items: [{ value: label, onRemove: reset }], onRemoveAll: reset });
    }
    if (features.finish !== false && foilFilter && foilFilter !== 'all') {
      const label = t(FOIL_FILTER_LABEL[foilFilter] || foilFilter);
      const reset = () => this.setState({ foilFilter: 'all' });
      groups.push({ key: 'finish', category: t('Finish'), items: [{ value: label, onRemove: reset }], onRemoveAll: reset });
    }
    return groups;
  }

  renderSearchSuggestions() {
    const sug = this._getSearchSuggestions();
    const { searchHighlightIdx } = this.state;
    if (sug.mode === 'hint') {
      return (
        <motion.div
          key="search-suggestions"
          {...SUGGESTIONS_MOTION}
          className="absolute left-0 z-40 mt-1 rounded-md text-[0.6875cqw]"
          style={{
            top: '100%',
            minWidth: '100%',
            maxWidth: 'calc(100cqw - 2cqw)',
            whiteSpace: 'nowrap',
            background: 'rgba(8,6,4,0.97)',
            border: `1px solid ${GOLD} 0.42)`,
            boxShadow: '0 8px 22px rgba(0,0,0,0.7)',
            color: '#d7c896',
            padding: '0.5cqw 0.625cqw',
          }}
        >
          <span className="opacity-70">{sug.hint}</span>
          <span className="ml-2 opacity-40">{t('Press Enter to apply')}</span>
        </motion.div>
      );
    }
    const items = sug.items;
    // An empty prefix list means the word being typed is a plain card-name
    // search, not a failed keyword. Stay out of the way. An empty value
    // list is a real dead end (`type:zzz`), so say so rather than blinking
    // the panel out and leaving the user guessing.
    const isValueMode = sug.mode === 'value' || sug.mode === 'element';
    if (items.length === 0 && !isValueMode) return null;
    const high = Math.min(searchHighlightIdx, items.length - 1);

    return (
      <motion.div
        key="search-suggestions"
        {...SUGGESTIONS_MOTION}
        className="absolute left-0 z-40 mt-1 rounded-md"
        style={{
          top: '100%',
          minWidth: '100%',
          width: 'max-content',
          maxWidth: 'calc(100cqw - 2cqw)',
          background: 'rgba(8,6,4,0.98)',
          border: `1px solid ${GOLD} 0.42)`,
          boxShadow: '0 8px 22px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,220,140,0.06)',
          overflow: 'hidden',
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div
          className="shrink-0 flex items-center justify-between gap-3 px-2.5 py-1.5 text-[0.5625cqw]"
          style={{ color: `${GOLD} 0.55)`, borderBottom: `1px solid ${GOLD} 0.18)`, whiteSpace: 'nowrap' }}
        >
          <span>{SUGGESTION_HEADINGS[sug.mode](sug.context)}</span>
          {items.length > 0 ? (
            <span
              className="normal-case tracking-normal font-mono px-1 rounded-sm"
              style={{ background: '#1c1610', color: '#d7c896', border: `1px solid ${GOLD} 0.28)`, fontSize: '0.5625cqw' }}
            >
              {t('Tab')}
            </span>
          ) : null}
        </div>
        {items.length === 0 ? (
          <div className="px-2.5 py-2 text-[0.6875cqw]" style={{ color: '#8f8264', whiteSpace: 'nowrap' }}>
            {t('No matches for “{q}”', { q: sug.context.valueFragment })}
          </div>
        ) : null}
        {/* The scroll container is the grid, and each row opts into its
            columns with `subgrid`, so the description column lines up on
            the widest prefix instead of a guessed min-width. `element:` /
            `el:` is nearly twice as wide as `set:` / `s:`. A fixed width
            either clipped it or left every short row over-padded. */}
        <div
          ref={this.suggestionListRef}
          className="grid overflow-y-auto overflow-x-hidden"
          style={{ maxHeight: PHONE ? 'min(50cqh, 16rem)' : 'min(60cqh, 20cqw)', gridTemplateColumns: 'max-content 1fr' }}
        >
          {/* Hover highlight rides mousemove, not mouseenter: arrowing down
              scrolls rows under a stationary cursor, which fires mouseenter
              and would rip the highlight back to wherever the pointer
              happens to be sitting. */}
          {items.map((item, i) => {
            const isHigh = i === high;
            return (
              <button
                key={sug.mode === 'prefix' ? item.prefix : item.label || item.insert}
                type="button"
                onMouseMove={() => {
                  if (this.state.searchHighlightIdx !== i) this.setState({ searchHighlightIdx: i });
                }}
                onClick={() => this._applySuggestion(item, sug.context, sug.mode)}
                className="grid items-baseline gap-x-3 px-2.5 py-1.5 text-[0.6875cqw] text-left cursor-pointer"
                style={{
                  gridColumn: '1 / -1',
                  gridTemplateColumns: 'subgrid',
                  background: isHigh ? `${GOLD} 0.16)` : 'transparent',
                  borderLeft: `2px solid ${isHigh ? `${GOLD} 0.7)` : 'transparent'}`,
                  color: isHigh ? '#f4e4b8' : '#d7c896',
                  transition: 'background-color 90ms ease, color 90ms ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {sug.mode === 'prefix' ? (
                  <>
                    <span className="font-mono font-semibold" style={{ color: '#f4e4b8' }}>
                      {item.prefix}:{item.aliases.length > 0 ? <span className="opacity-50 ml-1 text-[0.625cqw]">/ {item.aliases.join(', ')}:</span> : null}
                    </span>
                    <span className="opacity-75">{item.description}</span>
                  </>
                ) : (
                  <>
                    <span className="font-mono" style={{ color: item.detail ? '#f4e4b8' : undefined }}>{item.label}</span>
                    {item.detail ? <span className="opacity-75">{t(item.detail)}</span> : null}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>
    );
  }

  renderAdvancedPanel() {
    const {
      setFilters, rarityFilters, keywordFilters, subtypeFilters, umbrellaFilters,
      typeFilters, elementFilters, cardScope, foilFilter,
      rulesQuery, flavorQuery, artistFilters,
      costFilter, attackFilter, defenseFilter, lifeFilter,
      anyThresholdFilter, airThresholdFilter, earthThresholdFilter, fireThresholdFilter, waterThresholdFilter,
    } = this.state;
    const features = this.props.features || {};
    const { options: keywordOptions } = this._getKeywordIndex();
    const subtypeOptions = this._getSubtypeOptions();
    const setNum = (key, value) => this.setState({ [key]: value });
    const setText = (key, value) => this.setState({ [key]: value });
    const sectionLabel = (label) => (
      <div className={PHONE ? 'text-[0.75rem] font-bold mb-2' : 'text-[0.5625cqw] font-bold mb-2'} style={{ color: `${GOLD} 0.5)` }}>{label}</div>
    );
    const fieldLabel = (label) => (
      <div className={CAPTION_CLASS} style={CAPTION_STYLE}>{label}</div>
    );

    const pillRow = ({ label, values, filterKey, options, renderOption }) => (
      <div>
        {fieldLabel(label)}
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" {...chipProps(values.size === 0, PANEL_CHIP)} onClick={() => this.clearFilter(filterKey)}>{t('All')}</button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              {...chipProps(values.has(opt.value), `flex items-center gap-1 ${PANEL_CHIP}`)}
              onClick={() => this.toggleFilter(filterKey, opt.value)}
            >
              {renderOption ? renderOption(opt) : opt.label}
            </button>
          ))}
        </div>
      </div>
    );

    const toggleRow = ({ label, value, options, onChange }) => (
      <div>
        {fieldLabel(label)}
        <div className="flex flex-wrap items-center gap-1">
          {options.map((opt) => {
            const active = value === opt.value;
            // The Finish row's Foil option carries a custom activeColor
            // (cyan) so it reads as foil even when selected. Override
            // just the color when set. Every other state still comes
            // from .filter-chip.
            const colorOverride = active && opt.activeColor
              ? { color: opt.activeColor }
              : undefined;
            return (
              <button
                key={opt.value}
                type="button"
                {...chipProps(active, PANEL_CHIP)}
                style={colorOverride}
                onClick={() => onChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    const { advancedMaxHeight, advancedMaxWidth } = this.state;
    return (
      <motion.div
        key="advanced-panel"
        ref={(el) => { this._advancedPanelEl = el; }}
        initial={REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: -6 }}
        animate={REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        exit={REDUCED_MOTION
          ? { opacity: 0, transition: { duration: 0.1 } }
          : { opacity: 0, scale: 0.985, y: -4, transition: { duration: 0.12, ease: [0.22, 1, 0.36, 1] } }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        className={PHONE ? 'fixed inset-0 z-[80]' : 'absolute left-0 mt-2 z-50'}
        style={PHONE ? {
          // Full-screen sheet: no frame (a gold stroke fights the phone's
          // rounded screen corners), solid fill so the grid doesn't bleed
          // through, safe-area handled by the scroll container's padding.
          background: 'linear-gradient(180deg, #0e0b08 0%, #080604 100%)',
          boxShadow: 'inset 0 0 60px rgba(0,0,0,0.55)',
        } : {
          top: '100%',
          width: '40cqw',
          transformOrigin: 'top left',
          borderRadius: PANEL_RADIUS,
          maxWidth: advancedMaxWidth ?? 'calc(100cqw - 2cqw)',
          background: 'linear-gradient(180deg, #0e0b08 0%, #080604 100%)',
          border: `1px solid ${GOLD} 0.42)`,
          boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.1), inset 0 0 40px rgba(0,0,0,0.4), 0 10px 34px rgba(0,0,0,0.7)',
        }}
      >
        {!PHONE && (
          <>
            <CornerPlating position="top-left" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
            <CornerPlating position="top-right" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
            <CornerPlating position="bottom-left" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
            <CornerPlating position="bottom-right" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
          </>
        )}

        <div
          className={PHONE ? 'flex flex-col min-h-0 h-full' : 'flex flex-col min-h-0'}
          style={PHONE ? undefined : { maxHeight: advancedMaxHeight ?? 'calc(100cqh - 120px)' }}
        >
        <div
          className="min-h-0 overflow-y-auto"
          style={PHONE ? {
            padding: PANEL_PAD,
            paddingBottom: 0,
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))',
          } : { padding: PANEL_PAD, paddingBottom: 0 }}
        >
          <div className="stagger-in flex items-center justify-between mb-4" style={{ '--stagger': '40ms' }}>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="size-3.5" style={{ color: ACCENT_GOLD }} />
              <span className="arena-heading text-sm font-bold" style={{ color: '#f4e4b8', textShadow: '0 0 12px rgba(180,140,60,0.18)' }}>{t('Advanced Filters')}</span>
            </div>
            <button
              type="button"
              onClick={() => this.setState({ advancedOpen: false })}
              className="inline-flex items-center justify-center cursor-pointer transition-[background-color,color,scale] duration-100 active:scale-[0.96] hover:!bg-[rgba(180,60,60,0.35)] hover:!text-white"
              style={{ width: PHONE ? '2.75rem' : '2.5cqw', height: PHONE ? '2.75rem' : '2.5cqw', borderRadius: FIELD_RADIUS, background: 'transparent', color: 'rgba(215,200,150,0.7)' }}
              aria-label={t('Close advanced filters')}
            >
              <X className="size-3" />
            </button>
          </div>

          {/* Sections are separated by space, not hairlines. Four gold
              rules stacked in one panel is what made it read as busy. */}
          <div className="flex flex-col gap-4">
          {/* Set and Rarity pair off; Finish is three short chips and sits
              on its own line rather than forcing a third column (which is
              what kept the panel wide). */}
          <div className="stagger-in grid gap-x-4 gap-y-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '--stagger': '80ms' }}>
            {/* The phone strip is search-only, so everything the desktop
                bar shows as pills (element, type, scope) lives here. */}
            {PHONE ? pillRow({
              label: t('Element'),
              values: elementFilters,
              filterKey: 'elementFilters',
              options: [k('Water'), k('Earth'), k('Fire'), k('Air')].map((v) => ({ value: v, label: t(v) })),
              renderOption: (opt) => (
                <>
                  <SorceryElementIcon element={opt.value} className="size-3" />
                  {opt.label}
                </>
              ),
            }) : null}

            {PHONE && features.scope ? toggleRow({
              label: t('Show'),
              value: cardScope,
              options: [
                { value: 'deck', label: t('In Deck') },
                // Free play has no collection to narrow to.
                ...(isFreePlay() ? [] : [{ value: 'owned', label: t('Owned') }]),
                { value: 'all', label: t('All') },
              ],
              onChange: (v) => this.setState({ cardScope: v }),
            }) : null}

            {PHONE && features.type !== false ? pillRow({
              label: t('Type'),
              values: typeFilters,
              filterKey: 'typeFilters',
              options: [k('Avatar'), k('Minion'), k('Magic'), k('Aura'), k('Artifact'), k('Site')].map((v) => ({ value: v, label: t(v) })),
            }) : null}

            {pillRow({
              label: t('Set'),
              values: setFilters,
              filterKey: 'setFilters',
              options: ['beta', 'arthurian', 'gothic', 'dragonlord'].map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) })),
            })}

            {pillRow({
              label: t('Rarity'),
              values: rarityFilters,
              filterKey: 'rarityFilters',
              options: (Array.isArray(this.props.rarityOptions)
                ? this.props.rarityOptions
                : [k('Ordinary'), k('Exceptional'), k('Elite'), k('Unique')]
              ).map((v) => ({ value: v, label: t(v) })),
            })}

            {features.finish !== false ? toggleRow({
              label: t('Finish'),
              value: foilFilter,
              options: [
                { value: 'all', label: t('All') },
                { value: 'standard', label: t('Standard') },
                { value: 'foil', label: t('✦ Foil'), activeColor: '#6ec8d4' },
              ],
              onChange: (v) => this.setState({ foilFilter: v }),
            }) : null}
          </div>

          {/* Stats (2x2) and thresholds (3x2) sit side by side and end on
              the same row, so the block reads as one balanced band. */}
          <div className="stagger-in grid gap-x-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '--stagger': '120ms' }}>
            <div>
              {sectionLabel(t('Card Stats'))}
              <div className="grid gap-x-3 gap-y-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <NumericFilter label={t('Cost')}    value={costFilter}    onChange={(v) => setNum('costFilter', v)} />
                <NumericFilter label={t('Attack')}  value={attackFilter}  onChange={(v) => setNum('attackFilter', v)} />
                <NumericFilter label={t('Life')}    value={lifeFilter}    onChange={(v) => setNum('lifeFilter', v)} />
                <NumericFilter label={t('Defense')} value={defenseFilter} onChange={(v) => setNum('defenseFilter', v)} />
              </div>
            </div>

            <div>
              {sectionLabel(t('Thresholds'))}
              <div className="grid gap-x-3 gap-y-2" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                <NumericFilter label={t('Any')}   value={anyThresholdFilter}   onChange={(v) => setNum('anyThresholdFilter', v)}   max={9} />
                <NumericFilter label={t('Air')}   element="Air"   value={airThresholdFilter}   onChange={(v) => setNum('airThresholdFilter', v)}   max={9} />
                <NumericFilter label={t('Earth')} element="Earth" value={earthThresholdFilter} onChange={(v) => setNum('earthThresholdFilter', v)} max={9} />
                <NumericFilter label={t('Fire')}  element="Fire"  value={fireThresholdFilter}  onChange={(v) => setNum('fireThresholdFilter', v)}  max={9} />
                <NumericFilter label={t('Water')} element="Water" value={waterThresholdFilter} onChange={(v) => setNum('waterThresholdFilter', v)} max={9} />
              </div>
            </div>
          </div>

          <div className="stagger-in" style={{ '--stagger': '160ms' }}>
            {sectionLabel(t('Text Search'))}
            <div className="grid gap-x-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <TextFilter label={t('Rules')}  value={rulesQuery}  onChange={(v) => setText('rulesQuery', v)}  placeholder={t('e.g. draw, deal damage, charge')} />
              <TextFilter label={t('Flavor')} value={flavorQuery} onChange={(v) => setText('flavorQuery', v)} placeholder={t('Search flavor text…')} />
            </div>
          </div>

          <div className={PHONE ? 'stagger-in grid gap-x-3 gap-y-3' : 'stagger-in grid gap-x-3'} style={{ gridTemplateColumns: PHONE ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', '--stagger': '200ms' }}>
            <div>
              {fieldLabel(t('Subtype'))}
              <MultiSelect
                ariaLabel={t('Subtype filter')}
                options={subtypeOptions}
                value={Array.from(subtypeFilters)}
                onValueChange={(next) => this.setState({ subtypeFilters: new Set(next) })}
                placeholder={t('Any subtype')}
                menuSearchPlaceholder={t('Search subtypes…')}
                noOptionsMessage={t('No subtypes')}
                menuPreferredWidth={260}
                portalMenu
                triggerHeight={32}
                triggerStyle={FIELD_STYLE}
              />
            </div>
            <div>
              {fieldLabel(t('Keyword'))}
              <MultiSelect
                ariaLabel={t('Keyword filter')}
                options={keywordOptions}
                value={Array.from(keywordFilters)}
                onValueChange={(next) => this.setState({ keywordFilters: new Set(next) })}
                placeholder={t('Any keyword')}
                menuSearchPlaceholder={t('Search keywords…')}
                noOptionsMessage={t('No keywords')}
                menuPreferredWidth={280}
                portalMenu
                triggerHeight={32}
                triggerStyle={FIELD_STYLE}
              />
            </div>
            <div>
              {fieldLabel(t('Umbrella'))}
              <MultiSelect
                ariaLabel={t('Umbrella filter')}
                options={Object.keys(UMBRELLAS).map((u) => ({ value: u, label: t(UMBRELLA_LABEL[u] || u) }))}
                value={Array.from(umbrellaFilters)}
                onValueChange={(next) => this.setState({ umbrellaFilters: new Set(next) })}
                placeholder={t('Any umbrella')}
                menuSearchPlaceholder={t('Search…')}
                noOptionsMessage={t('No umbrellas')}
                menuPreferredWidth={220}
                portalMenu
                triggerHeight={32}
                triggerStyle={FIELD_STYLE}
              />
            </div>
            <div>
              {fieldLabel(t('Artist'))}
              <MultiSelect
                ariaLabel={t('Artist filter')}
                options={this._getArtistOptions()}
                value={Array.from(artistFilters)}
                onValueChange={(next) => this.setState({ artistFilters: new Set(next) })}
                placeholder={t('Any artist')}
                menuSearchPlaceholder={t('Search artists…')}
                noOptionsMessage={t('No artists')}
                menuPreferredWidth={260}
                portalMenu
                triggerHeight={32}
                triggerStyle={FIELD_STYLE}
              />
            </div>
          </div>

          </div>
        </div>

        <div
          className="stagger-in shrink-0 flex items-center justify-end gap-2"
          style={PHONE ? {
            padding: PANEL_PAD,
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            borderTop: `1px solid ${GOLD} 0.08)`,
            '--stagger': '240ms',
          } : { padding: PANEL_PAD, '--stagger': '240ms' }}
        >
            {/* Phone: the sort selector (bottomTrailing) has no bar row to
                live in, so it docks into the sheet footer's left side. */}
            {PHONE && this.props.bottomTrailing ? (
              <div className="mr-auto min-w-0">{this.props.bottomTrailing}</div>
            ) : null}
            <Button type="button" variant="ghost" size="sm" onClick={this._resetAllFilters} className="font-semibold active:scale-[0.96]" style={{ borderRadius: FIELD_RADIUS }}>{t('Reset')}</Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => this.setState({ advancedOpen: false })}
              className="font-bold active:scale-[0.96] !bg-[rgba(212,168,67,0.28)] hover:!bg-[rgba(212,168,67,0.45)] !text-[#f4e4b8]"
              style={{
                border: `1px solid ${GOLD} 0.55)`,
                borderRadius: FIELD_RADIUS,
                boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.2), 0 0 12px rgba(212,168,67,0.22)',
              }}
            >
              Done
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  render() {
    const { searchQuery, advancedOpen, elementFilters, typeFilters, cardScope } = this.state;
    const features = this.props.features || {};
    const chipGroups = this._buildActiveChips();
    // Elements are desktop bar pills; on phone they live in the sheet,
    // so they have to count toward the filter button's badge.
    const activeCount = chipGroups.reduce((sum, g) => sum + g.items.length, 0)
      + (PHONE ? elementFilters.size : 0);
    const anyActive = activeCount > 0 || !!searchQuery.trim() || elementFilters.size > 0 || typeFilters.size > 0;

    // Phone hosts can hand the bar a `portalTarget` element (the deck
    // editor's header slot). The bar then renders frameless into that
    // slot instead of occupying its own row, and contributes zero height
    // to the host's layout. All state/wiring stays here.
    const portaled = PHONE && this.props.portalTarget;

    const bar = (
      <div
        className={portaled ? 'flex flex-col min-w-0 w-full' : cn('rounded-xl p-2.5 flex flex-col gap-2', this.props.className)}
        style={portaled ? undefined : {
          background: '#0e0b08',
          border: `1px solid ${GOLD} 0.18)`,
          ...this.props.style,
        }}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div ref={(el) => { this._advancedAnchorEl = el; }} className="relative" style={PHONE ? { flex: '1 1 auto', minWidth: 0 } : { maxWidth: '26.25cqw', flexBasis: '26.25cqw', flexShrink: 1 }}>
            <div
              className="flex items-stretch rounded-lg overflow-hidden transition-[border-color,box-shadow] duration-150"
              style={{
                height: PHONE ? '2.5rem' : '2.125cqw',
                background: '#080604',
                border: `1px solid ${GOLD} ${advancedOpen ? '0.42' : '0.22'})`,
                boxShadow: advancedOpen
                  ? 'inset 0 1px 0 rgba(255,220,140,0.08), 0 0 14px rgba(212,168,67,0.14)'
                  : 'inset 0 1px 0 rgba(255,220,140,0.03)',
              }}
            >
              <div className="relative flex-1 min-w-0 flex items-center">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none size-3.5"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ color: `${GOLD} 0.5)` }}
                >
                  <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  ref={this.searchInputRef}
                  type="text"
                  placeholder={this.props.placeholder || (PHONE ? 'Search cards…' : 'Search cards… (try cost:3, t:Minion, r:draw)')}
                  value={searchQuery}
                  onInput={(e) => this.setState({
                    searchQuery: e.target.value,
                    searchCursor: e.target.selectionStart || 0,
                    searchSuggestionsOpen: true,
                    searchHighlightIdx: 0,
                  })}
                  onKeyDown={this._handleSearchKeyDown}
                  onFocus={(e) => this.setState({ searchSuggestionsOpen: true, searchCursor: e.target.selectionStart || 0 })}
                  onBlur={() => { setTimeout(() => this._commitSearchTokens(), 160); }}
                  onSelect={(e) => this.setState({ searchCursor: e.target.selectionStart || 0 })}
                  className={cn('flex-1 min-w-0 h-full pl-9 text-xs outline-none bg-transparent', searchQuery ? 'pr-8' : 'pr-3')}
                  style={{ color: TEXT_BODY }}
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      this.setState({ searchQuery: '', searchCursor: 0, searchHighlightIdx: 0 }, () => {
                        this.searchInputRef.current?.focus();
                      });
                    }}
                    aria-label="Clear search"
                    title="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-full transition-colors cursor-pointer"
                    style={{
                      width: PHONE ? '1.5rem' : '1cqw', height: PHONE ? '1.5rem' : '1cqw',
                      background: 'rgba(0,0,0,0.45)',
                      border: `1px solid ${GOLD} 0.25)`,
                      color: '#d7c896',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(180,60,60,0.45)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.45)'; e.currentTarget.style.color = '#d7c896'; }}
                  >
                    <X className="size-2.5" strokeWidth={2.6} />
                  </button>
                ) : null}
              </div>

              <div className="w-px shrink-0" style={{ background: `${GOLD} 0.2)` }} />

              <button
                ref={(el) => { this._advancedButtonEl = el; }}
                type="button"
                onClick={() => this.setState((s) => ({ advancedOpen: !s.advancedOpen }))}
                aria-label={advancedOpen ? 'Close advanced filters' : 'Open advanced filters'}
                title={advancedOpen ? 'Close advanced filters' : 'Advanced filters'}
                className="relative shrink-0 h-full inline-flex items-center justify-center transition-all cursor-pointer"
                style={{
                  padding: PHONE ? '0 0.75rem' : '0 0.75cqw',
                  background: advancedOpen ? `${GOLD} 0.18)` : 'transparent',
                  color: advancedOpen ? '#f4e4b8' : '#d7c896',
                }}
                onMouseEnter={(e) => { if (!advancedOpen) e.currentTarget.style.background = 'rgba(212,168,67,0.08)'; }}
                onMouseLeave={(e) => { if (!advancedOpen) e.currentTarget.style.background = 'transparent'; }}
              >
                <SlidersHorizontal className="size-3.5" />
                {activeCount > 0 ? (
                  <span
                    className="absolute inline-flex items-center justify-center rounded-full text-[0.5625cqw] font-bold tabular-nums"
                    style={{
                      ...(PHONE
                        ? { top: '0.25rem', right: '0.125rem', minWidth: '1rem', height: '1rem', padding: '0 0.1875rem' }
                        : { top: '0.25cqw', right: '0.125cqw', minWidth: '0.875cqw', height: '0.875cqw', padding: '0 0.1875cqw' }),
                      background: ACCENT_GOLD, color: '#1a1410',
                      boxShadow: '0 0 6px rgba(212,168,67,0.6), 0 0 0 1.5px #080604',
                    }}
                  >
                    {activeCount}
                  </span>
                ) : null}
              </button>
            </div>
            <AnimatePresence>
              {this.state.searchSuggestionsOpen && !advancedOpen ? this.renderSearchSuggestions() : null}
            </AnimatePresence>
            <AnimatePresence>
              {advancedOpen ? this.renderAdvancedPanel() : null}
            </AnimatePresence>
          </div>

          {!PHONE ? (
            <div className="flex items-center gap-0.5">
              <button type="button" {...chipProps(elementFilters.size === 0)} onClick={() => this.clearFilter('elementFilters')}>All</button>
              {['Water', 'Earth', 'Fire', 'Air'].map((el) => (
                <button
                  key={el}
                  type="button"
                  title={el}
                  aria-label={el}
                  {...chipProps(elementFilters.has(el), 'flex items-center')}
                  onClick={() => this.toggleFilter('elementFilters', el)}
                >
                  <SorceryElementIcon element={el} className="size-3" />
                </button>
              ))}
            </div>
          ) : null}

          {features.type !== false && !PHONE ? (
            <div className="flex items-center gap-0.5">
              <button type="button" {...chipProps(typeFilters.size === 0)} onClick={() => this.clearFilter('typeFilters')}>All</button>
              {['Avatar', 'Minion', 'Magic', 'Aura', 'Artifact', 'Site'].map((t) => (
                <button
                  key={t}
                  type="button"
                  {...chipProps(typeFilters.has(t))}
                  onClick={() => this.toggleFilter('typeFilters', t)}
                >
                  {t}
                </button>
              ))}
            </div>
          ) : null}

          {features.scope && !PHONE ? (
            <div className="ml-auto flex items-center gap-0.5">
              {[
                { value: 'deck', label: 'In Deck' },
                ...(isFreePlay() ? [] : [{ value: 'owned', label: 'Owned' }]),
                { value: 'all', label: 'All' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  {...chipProps(cardScope === opt.value)}
                  onClick={() => this.setState({ cardScope: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}

          {this.props.trailing ? <div className={features.scope ? 'flex items-center gap-2' : 'ml-auto flex items-center gap-2'}>{this.props.trailing}</div> : null}
        </div>

        {/* Row 2 (active chips + sort + reset) is desktop-only: on phone
            every filter lives in the sheet and the badge on the filter
            button carries the active count. */}
        {!PHONE ? <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {chipGroups.map((g) => {
            if (g.items.length === 1) {
              const item = g.items[0];
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={item.onRemove}
                  title={`Remove ${g.category}: ${item.value}`}
                  className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[0.6875cqw] font-semibold transition-all cursor-pointer"
                  style={{
                    background: 'rgba(12,10,8,0.85)',
                    border: `1px solid ${GOLD} 0.3)`,
                    color: '#d7c896',
                    lineHeight: 1.2,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.15)`; e.currentTarget.style.borderColor = `${GOLD} 0.55)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(12,10,8,0.85)'; e.currentTarget.style.borderColor = `${GOLD} 0.3)`; }}
                >
                  <span className="opacity-55 text-[0.625cqw]">{g.category}</span>
                  <span className="text-[0.6875cqw]" style={{ color: '#f4e4b8' }}>{item.value}</span>
                  <X className="size-2.5 opacity-55 group-hover:opacity-100 -mr-0.5" />
                </button>
              );
            }
            return (
              <GroupedChip
                key={g.key}
                category={g.category}
                items={g.items}
                onRemoveAll={g.onRemoveAll}
              />
            );
          })}
          <div className="ml-auto shrink-0 flex items-center gap-3">
            {this.props.bottomTrailing}
            <button
              type="button"
              onClick={this._resetAllFilters}
              disabled={!anyActive}
              className="filter-chip shrink-0 text-[0.625cqw] font-semibold"
              data-active={anyActive ? 'true' : 'false'}
              style={anyActive ? { color: ACCENT_GOLD } : { color: `${GOLD} 0.25)` }}
            >
              Reset
            </button>
          </div>
        </div> : null}
      </div>
    );

    return portaled ? createPortal(bar, this.props.portalTarget) : bar;
  }
}

function coerceSet(v) {
  if (!v) return null;
  if (v instanceof Set) return v;
  if (Array.isArray(v)) return new Set(v);
  return null;
}

// Shallow comparison of filter-state fields we care about. setState
// can produce referentially new Sets even when their contents match
// an earlier render; a full deep compare would hurt more than it helps.
function filterStateChanged(a, b) {
  const keys = [
    'searchQuery', 'cardScope', 'foilFilter',
    'elementFilters', 'typeFilters', 'setFilters', 'rarityFilters',
    'keywordFilters', 'subtypeFilters', 'umbrellaFilters', 'artistFilters',
    'rulesQuery', 'flavorQuery',
    'costFilter', 'attackFilter', 'defenseFilter', 'lifeFilter',
    'anyThresholdFilter', 'airThresholdFilter', 'earthThresholdFilter',
    'fireThresholdFilter', 'waterThresholdFilter',
  ];
  for (const k of keys) if (a[k] !== b[k]) return true;
  return false;
}
